import { LifeTransactionType } from '@tymbe/schema/enums';
import { use, useCallback, useRef } from 'react';
import { ModalContext } from './index.page';
import { TyTimelineLivesItem } from './TyTimelineLivesItem';
import { useLifeTransactions } from '../components/useLifeTransactions';
import { useParams } from 'react-router-dom';
import Spinner from '@tymbe/ty-components/indicators/Spinner';
import { PrimaryButton } from '@tymbe/ui-primitives';

export const TyTimeline = () => {
  const { toggleAttendanceDetail } = use(ModalContext);
  const { personId } = useParams();
  const { isFetching, hasNextPage, fetchNextPage, isError, data, isSuccess } =
    useLifeTransactions(personId!);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastItemObserverRef = useCallback(
    (node: HTMLElement) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        async (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            await fetchNextPage();
          }
        },
        {
          threshold: 0,
        }
      );
      // Re-scan, find and observe the new node.
      if (node) observer.current.observe(node);
    },
    [isFetching, hasNextPage]
  );

  // Fetch failed
  if (isError) {
    return (
      <div className="flex items-center justify-center mb-5">
        Ups, něco se pokazilo :(
      </div>
    );
  }

  // Initial fetch where we don't have any data yet
  if (data?.pages === undefined && isFetching) {
    return (
      <div className="flex items-center justify-center mb-5">
        <Spinner className="w-[20px] h-[20px]" />
      </div>
    );
  }

  // No data found
  if (isSuccess && data?.pages[0].total === 0) {
    return (
      <div className="flex items-center justify-center mb-5">
        Nenalezeny žádné záznamy
      </div>
    );
  }

  return (
    <>
      {isFetching ? (
        <div className="flex flex-row items-center justify-center mb-5 gap-2">
          <Spinner className="w-[20px] h-[20px]" /> Data se aktualizují
        </div>
      ) : (
        ''
      )}

      <ol className="relative border-s-2 border-fg-100 pl-10 userInfo">
        {data?.pages.map((page) =>
          page.data.map((lifeTransaction, i) => {
            const hasApplication = [
              LifeTransactionType.APPROVED_EXCUSE,
              LifeTransactionType.CANCELED_LATE_SHIFT,
              LifeTransactionType.UNEXCUSED_ABSENCE,
              LifeTransactionType.RESCUE_SHIFT,
            ].includes(lifeTransaction.type);

            const action = hasApplication ? (
              <PrimaryButton
                onClick={() =>
                  toggleAttendanceDetail(lifeTransaction.application ?? null)
                }
                className="!text-caption-lg !font-semibold"
              >
                Zobrazit směnu
              </PrimaryButton>
            ) : undefined;

            const lastItemObserverRefProp =
              i + 1 === page.data.length ? lastItemObserverRef : null;

            return (
              <TyTimelineLivesItem
                key={lifeTransaction.id}
                lifeTransaction={lifeTransaction}
                lastItemObserverRef={lastItemObserverRefProp}
                action={action}
              />
            );
          })
        )}
      </ol>
      {/*
      // Fetching next page
      */}
      {isFetching && (
        <div className="flex items-center justify-center mb-5">
          <Spinner className="w-[20px] h-[20px]" />
        </div>
      )}
    </>
  );
};
